import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Users, TrendingUp, BarChart3, Sparkles, ChevronDown, ChevronUp, ArrowLeft, Loader2, ExternalLink } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getScoreColor(score) {
  if (score >= 75) return "#22c55e";
  if (score >= 50) return "#3b82f6";
  if (score >= 25) return "#f59e0b";
  return "#ef4444";
}

export default function AdminDashboard({ onBack }) {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [leadsRes, statsRes] = await Promise.all([
          axios.get(`${API}/leads`),
          axios.get(`${API}/leads/stats`)
        ]);
        setLeads(leadsRes.data);
        setStats(statsRes.data);
      } catch (e) {
        console.error("Failed to fetch leads:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-loading">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <div className="border-b border-zinc-800 bg-[#0f0f10]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={onBack} className="text-zinc-500 hover:text-white transition-colors" data-testid="admin-back-button">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-xl font-black tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>AI Search Audit — Leads</h1>
              <p className="text-xs text-zinc-500">by Geokath Marketing</p>
            </div>
          </div>
          <span className="text-xs uppercase tracking-[0.2em] font-bold text-orange-500">Admin</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <div className="border border-zinc-800 rounded-lg p-4 bg-[#1a1a1c]/50">
              <Users className="w-4 h-4 text-orange-500 mb-2" />
              <div className="text-2xl font-black text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{stats.total_leads}</div>
              <div className="text-xs text-zinc-500">Total Leads</div>
            </div>
            <div className="border border-zinc-800 rounded-lg p-4 bg-[#1a1a1c]/50">
              <TrendingUp className="w-4 h-4 text-orange-500 mb-2" />
              <div className="text-2xl font-black text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{stats.avg_overall}%</div>
              <div className="text-xs text-zinc-500">Avg Overall Score</div>
            </div>
            <div className="border border-zinc-800 rounded-lg p-4 bg-[#1a1a1c]/50">
              <BarChart3 className="w-4 h-4 text-orange-500 mb-2" />
              <div className="text-2xl font-black text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{stats.avg_seo}%</div>
              <div className="text-xs text-zinc-500">Avg SEO Score</div>
            </div>
            <div className="border border-zinc-800 rounded-lg p-4 bg-[#1a1a1c]/50">
              <Sparkles className="w-4 h-4 text-orange-500 mb-2" />
              <div className="text-2xl font-black text-white" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{stats.avg_ai}%</div>
              <div className="text-xs text-zinc-500">Avg AI Visibility</div>
            </div>
          </div>
        )}

        <div className="border border-zinc-800 rounded-lg overflow-hidden" data-testid="leads-table">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800 bg-[#1a1a1c]/50">
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-semibold px-4 py-3">Name</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-semibold px-4 py-3 hidden md:table-cell">Email</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-semibold px-4 py-3 hidden lg:table-cell">URL</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-semibold px-4 py-3">Overall</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-semibold px-4 py-3 hidden md:table-cell">SEO</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-semibold px-4 py-3 hidden md:table-cell">AI</th>
                <th className="text-left text-xs uppercase tracking-wider text-zinc-500 font-semibold px-4 py-3 hidden lg:table-cell">Date</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {leads.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-zinc-500 py-12">No leads yet. Share your audit tool to start capturing leads.</td>
                </tr>
              ) : (
                leads.map((lead, i) => (
                  <motion.tr key={lead.id} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: i * 0.03 }}
                    className="border-b border-zinc-800/50 hover:bg-[#1a1a1c]/30 cursor-pointer"
                    onClick={() => setExpanded(expanded === lead.id ? null : lead.id)}>
                    <td className="px-4 py-3">
                      <div className="text-sm font-semibold text-white">{lead.lead_info?.name}</div>
                      <div className="text-xs text-zinc-500 md:hidden">{lead.lead_info?.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-400 hidden md:table-cell">{lead.lead_info?.email}</td>
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <a href={lead.lead_info?.url} target="_blank" rel="noopener noreferrer" className="text-sm text-zinc-400 hover:text-orange-500 flex items-center gap-1"
                        onClick={e => e.stopPropagation()}>
                        {lead.lead_info?.url?.replace(/https?:\/\//, '').slice(0, 25)} <ExternalLink className="w-3 h-3" />
                      </a>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-lg font-black" style={{ color: getScoreColor(lead.overall_score), fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                        {lead.overall_score}%
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold hidden md:table-cell" style={{ color: getScoreColor(lead.seo_score) }}>
                      {lead.seo_score}%
                    </td>
                    <td className="px-4 py-3 text-sm font-semibold hidden md:table-cell" style={{ color: getScoreColor(lead.ai_visibility?.overall_ai_score || 0) }}>
                      {lead.ai_visibility?.overall_ai_score || 0}%
                    </td>
                    <td className="px-4 py-3 text-sm text-zinc-500 hidden lg:table-cell">
                      {new Date(lead.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3">
                      {expanded === lead.id ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>

          {expanded && leads.find(l => l.id === expanded) && (() => {
            const lead = leads.find(l => l.id === expanded);
            return (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                className="border-t border-zinc-800 bg-[#1a1a1c]/30 px-6 py-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">AI Analysis</h4>
                    <p className="text-sm text-zinc-400">{lead.ai_visibility?.ai_summary || "N/A"}</p>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">AI Scores</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-zinc-600">ChatGPT:</span> <span className="text-white">{lead.ai_visibility?.chatgpt_score || 0}%</span></p>
                      <p><span className="text-zinc-600">Perplexity:</span> <span className="text-white">{lead.ai_visibility?.perplexity_score || 0}%</span></p>
                      <p><span className="text-zinc-600">Gemini:</span> <span className="text-white">{lead.ai_visibility?.gemini_score || 0}%</span></p>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Recommendations</h4>
                    <div className="flex flex-wrap gap-1.5">
                      {lead.recommendations?.map((r, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 rounded bg-orange-500/10 text-orange-500 border border-orange-500/20">
                          {r.title}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
                {lead.quiz_answers?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-zinc-800/50">
                    <h4 className="text-xs font-bold text-zinc-500 uppercase tracking-wider mb-2">Quiz Responses</h4>
                    <div className="flex flex-wrap gap-2">
                      {lead.quiz_answers.map((a, idx) => (
                        <span key={idx} className="text-xs px-2 py-1 rounded bg-zinc-800 text-zinc-400">
                          {a.question_id}: {a.answer} ({a.score}pts)
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
